'use client';

import type { Dictionary } from '@ts/dictionary';
import type { LoopFetchProductResult } from '@ts/loopFetchProductResult';
import { formatPrice } from '@utils/currency/formatPrice';
import useVariant from '@utils/hooks/product/useVariant';
import useLightsources from '@utils/hooks/useLightSources';
import useProductSearchParams from '@utils/hooks/useProductSearchParams';
import Image from 'next/image';
import Lightsource from '../Lightsource/Lightsource';
import { useEffect, useMemo, useState } from 'react';
import mapLightSourcesForSpecificVariant from '@utils/product/mapAndSortLightSourcesForSpecificVariant';
import StockStatus from '@ui/components/StockStatus/StockStatus';
import Icon from '@ui/components/icon/icon';
import Link from '@ui/components/Link/Link';
import { Transition } from '@headlessui/react';
import getStockStatusType from '@utils/stockStatus/getStockStatusType';
import { getStockStatusDictionary } from '@utils/stockStatus/getStockStatusDictionary';
import { cn } from '@ngg/storefront-utils';
import Paragraph from '../Paragraph/Paragraph';

export default function LightSourceSelector({
  dictionary,
  data,
  market,
  className,
}: {
  dictionary: Dictionary;
  data: LoopFetchProductResult;
  market: string;
  className?: string;
}) {
  const { product } = data;
  const setProductSearchParams = useProductSearchParams();

  const variant = useVariant(data);
  const [selectedLightsourceId, setSelectedLightsourceId] = useState<
    string | null
  >();

  const { data: lightsourceData } = useLightsources({
    product: product,
    market,
  });

  const structuredLightSourceData = useMemo(() => {
    if (!lightsourceData || !lightsourceData.length) return undefined;
    return mapLightSourcesForSpecificVariant(
      lightsourceData,
      product.recommendedLightsources,
    );
  }, [lightsourceData, product.recommendedLightsources]);

  useEffect(() => {
    if (!variant) return;
    if (!structuredLightSourceData?.length) return;
    if (selectedLightsourceId) return;
    const cheapestLightsource = structuredLightSourceData[0];
    setSelectedLightsourceId(cheapestLightsource?.id);
  }, [selectedLightsourceId, structuredLightSourceData, variant]);

  useEffect(() => {
    if (!selectedLightsourceId) return;
    setProductSearchParams({
      type: 'lightsource',
      lightsourceId: selectedLightsourceId,
    });
  }, [selectedLightsourceId, setProductSearchParams]);

  if (!structuredLightSourceData) return null;

  return (
    <Transition
      as="section"
      enter="transition ease duration-500 transform"
      enterFrom="opacity-0 translate-x-12"
      enterTo="opacity-100 translate-x-0"
      leave="transition ease duration-300 transform"
      leaveFrom="opacity-100 translate-x-0"
      leaveTo="opacity-0 translate-x-12"
      show={!!variant}>
      <div className={cn('mb-4 mt-6 font-sans', className)}>
        <Paragraph className="mb-4 text-sm font-bold">
          {dictionary.productComesWithoutLightSourceAdd}
        </Paragraph>
        {structuredLightSourceData?.map((item) => {
          return (
            <Lightsource
              key={item.id}
              checked={selectedLightsourceId === item.id}
              onChange={() => {
                setSelectedLightsourceId(item.id);
              }}
              id={item.id}
              renderImage={() =>
                item.image ? (
                  <Image
                    className="object-contain mix-blend-multiply"
                    src={item.image?.src}
                    alt={item.image?.alt || ''}
                    width={100}
                    height={100}
                  />
                ) : null
              }
              renderContent={() => (
                <div className="flex h-full w-full items-stretch justify-between">
                  <div className="p-3 md:space-y-1.5">
                    <div className="items-between flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        <Paragraph className="text-xs font-bold">
                          {item.norce_brand}
                        </Paragraph>
                        <Paragraph className="text-sm">{item.title}</Paragraph>
                        <div className="flex whitespace-nowrap text-sm">
                          <span className="font-bold">
                            {`${
                              variant?.lightsourceQuantity ||
                              product?.lightsourceQuantity ||
                              1 // According to PIM we should assume quantity 1 if not specified
                            }
                        x
                      ${formatPrice(item.price)}`}
                          </span>
                          {item.campaignPrice && (
                            <span className="text-grey-300 ml-1 line-through">
                              {formatPrice(item.originalPrice)}
                            </span>
                          )}
                        </div>
                      </div>
                      <StockStatus
                        status={getStockStatusType([item.stockStatus])}
                        stockStatusDictionary={getStockStatusDictionary(
                          dictionary,
                        )}
                        className="gap-1 font-serif text-sm italic"
                      />
                    </div>
                  </div>

                  <div className="space-y-1 p-3 text-right md:self-auto">
                    <div className="items-between flex h-full flex-col justify-between">
                      {item.ProductURL && (
                        <Link href={item.ProductURL}>
                          <Paragraph className="text-xs underline">
                            {dictionary.details}
                          </Paragraph>
                        </Link>
                      )}
                      {item.isRecommended && (
                        <div className="flex items-center md:static">
                          <Icon
                            name="greenCheckmark"
                            className="mr-1 text-base"
                          />
                          <Paragraph className="text-xs font-bold uppercase">
                            {dictionary.recommended}
                          </Paragraph>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            />
          );
        })}
        <Lightsource
          id="no-lightsource"
          checked={selectedLightsourceId === 'no-lightsource'}
          onChange={() => {
            setSelectedLightsourceId('no-lightsource');
          }}
          renderImage={() => (
            <div className="flex h-full w-full items-center justify-center bg-beige">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 64 64"
                fill="none">
                <path
                  d="M31.9886 14.8691C37.6627 14.8691 42.2665 19.5412 42.2665 25.2729C42.2665 28.1489 41.5677 29.8091 40.7929 31.1673C40.0179 32.5254 39.2073 33.5523 38.8281 35.3744C38.7829 35.5935 38.5685 35.7342 38.3494 35.6889C38.1302 35.6437 37.9894 35.4296 38.0346 35.2104C38.4483 33.2225 39.3598 32.0619 40.1002 30.7644C40.8406 29.4669 41.4604 28.0232 41.4604 25.2732C41.4604 19.972 37.2218 15.6758 31.9889 15.6758C28.4768 15.6758 25.4129 17.6146 23.7766 20.4875C23.7302 20.5921 23.6412 20.6717 23.5325 20.7069C23.4235 20.7421 23.3048 20.7294 23.2058 20.6717C23.1069 20.6142 23.0371 20.5173 23.0137 20.4052C22.9906 20.2931 23.016 20.1765 23.0839 20.0842C24.8573 16.9706 28.1804 14.8691 31.9886 14.8691ZM18.4617 17.6904C18.5535 17.7035 18.6381 17.7481 18.701 17.8164L31.2712 30.3737L45.8434 44.946C45.9324 45.0179 45.9872 45.1241 45.9932 45.2383C45.9995 45.3527 45.9568 45.4641 45.8757 45.545C45.7949 45.626 45.6834 45.6687 45.5691 45.6625C45.4549 45.6564 45.3486 45.6021 45.2768 45.5129L38.4373 38.6734L37.0015 46.1172C36.8662 46.8188 36.1715 47.2507 35.4396 47.2507H35.2758V47.5026C35.2758 48.3617 34.6867 49.1274 33.8652 49.1274H30.1119C29.2905 49.1274 28.7013 48.3618 28.7013 47.5026V47.2507H28.5375C27.8056 47.2507 27.1233 46.819 26.9884 46.1172L25.4013 37.8548C25.3969 37.8466 25.3927 37.8383 25.3888 37.8298V37.8173C25.3708 37.7689 25.3623 37.7177 25.3636 37.666C25.3636 34.6806 24.5152 33.3704 23.6003 31.8596C22.6851 30.349 21.7109 28.6577 21.7109 25.2719C21.7109 24.2664 21.8625 23.2958 22.1265 22.375L18.1338 18.3823C18.0071 18.2625 17.9719 18.075 18.0465 17.9173C18.1211 17.7596 18.2887 17.6687 18.4617 17.6904ZM22.7819 23.0306C22.6106 23.7517 22.5173 24.4971 22.5173 25.2725C22.5173 28.5098 23.3831 29.9419 24.2931 31.4442C25.1589 32.8736 26.0444 34.412 26.1321 37.2634H37.0142L22.7819 23.0306ZM37.7451 38.0696H26.2459L26.6742 40.2485C26.6786 40.2483 26.6827 40.2483 26.6869 40.2485H34.0426C34.0551 40.2479 34.0679 40.2479 34.0804 40.2485C34.3024 40.2593 34.4739 40.4481 34.4635 40.6704C34.4531 40.8925 34.2647 41.0643 34.0426 41.0545H26.8256L27.2539 43.2587H31.675C31.6916 43.2577 31.7083 43.2577 31.7252 43.2587C31.946 43.2747 32.1129 43.4656 32.0989 43.6866C32.0852 43.9077 31.8958 44.0764 31.6748 44.0647H27.4177L27.7829 45.9666C27.819 46.1539 28.1552 46.4452 28.5385 46.4452H35.4409C35.8243 46.4452 36.173 46.1539 36.2093 45.9666L37.708 38.1703C37.7162 38.1351 37.7291 38.1012 37.7462 38.0695L37.7451 38.0696ZM34.4703 47.2514H29.5076V47.5033C29.5076 48.0098 29.8184 48.3221 30.1122 48.3221H33.8655C34.1592 48.3221 34.4701 48.01 34.4701 47.5033L34.4703 47.2514Z"
                  fill="#757575"
                />
              </svg>
            </div>
          )}
          renderContent={() => (
            <Paragraph className="p-3 text-sm">
              {dictionary.noLightSourcePlease}
            </Paragraph>
          )}
        />
      </div>
    </Transition>
  );
}
